/**
 * PeeringDB — Internet Exchange proximity for latency assessment.
 * Free API key (register at peeringdb.com).
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout, haversineKm } from './types';
import { raceWithAbort, rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

export interface ConnectivityData {
  nearestIxName: string;
  nearestIxCity: string;
  nearestIxDistKm: number;
  ixCount50km: number;       // Number of IXs within 50km
  facilityCount50km: number; // Number of colo/DC facilities within 50km
  /**
   * CONNECTIVITY-ADVISORY — whether `nearestIx*` describe a true IX or the
   * nearest-colo-facility fallback (IX list failed; see `usedIx` below). A
   * 'colo-fallback' reading is display-only; scoring excludes it.
   * Mirrors `ConnectivityEvidence.nearestIxKind`.
   *
   * OPTIONAL, deliberately: a live fetch ALWAYS sets it, but this type also
   * describes payloads that predate the field (cached provider results,
   * persisted SED sections, fixtures). Requiring it would assert that every
   * ConnectivityData in the system carries the label, which is false for
   * anything stored before this change — and would contradict the documented
   * `undefined ⇒ treated as 'ixp'` contract on the sibling declarations in
   * `types/siteRag.ts` and `types/siteEvidence.ts`.
   */
  nearestIxKind?: 'ixp' | 'colo-fallback';
}

// ── Global cache for US IX and facility data (changes infrequently) ──
// Avoids re-fetching hundreds of records on each report generation.
// Auto-evicts after TTL to free memory (~1.3MB for facilities).
let _ixCache: { data: any[]; ts: number } | null = null;
let _facCache: { data: any[]; ts: number } | null = null;
let _ixFetchPromise: Promise<any[]> | null = null;
let _facFetchPromise: Promise<any[]> | null = null;
const GLOBAL_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours
const MAX_CACHED_ENTRIES = 5000; // Safety cap — US has ~2500 facilities

// Auto-evict expired caches to free memory
function evictExpired() {
  if (_ixCache && Date.now() - _ixCache.ts > GLOBAL_CACHE_TTL) _ixCache = null;
  if (_facCache && Date.now() - _facCache.ts > GLOBAL_CACHE_TTL) _facCache = null;
}
// Check every 30 minutes.
// BUG-2 FIX — a module-scope, ref'd setInterval is a libuv handle that keeps
// the Node event loop alive forever, so a one-shot Cloud Run job never exits
// after its work resolves (the post-completion hang). unref() the handle so it
// can never pin the loop open; the in-memory cache is irrelevant to a one-shot
// job, so dropping the eviction tick in a draining process is harmless.
const _evictTimer =
  typeof setInterval !== 'undefined' ? setInterval(evictExpired, 30 * 60 * 1000) : null;
_evictTimer?.unref?.();

async function getIxList(): Promise<any[]> {
  if (_ixCache && Date.now() - _ixCache.ts < GLOBAL_CACHE_TTL) return _ixCache.data;
  if (_ixFetchPromise) return _ixFetchPromise;
  _ixFetchPromise = (async () => {
    // Try US-filtered first, then unfiltered as fallback
    const urls = [
      `https://www.peeringdb.com/api/ix?country=US&depth=0`,
      `https://www.peeringdb.com/api/ix?depth=0`,
    ];
    for (const url of urls) {
      try {
        const res = await fetchWithTimeout(url, 25000);
        if (!res.ok) continue;
        const text = await res.text();
        if (text.startsWith('<') || !text.startsWith('{')) continue;
        const json = JSON.parse(text);
        const ixes = (json.data || []).filter((ix: any) => ix.latitude && ix.longitude).slice(0, MAX_CACHED_ENTRIES);
        if (ixes.length > 0) {
          _ixCache = { data: ixes, ts: Date.now() };
          _ixFetchPromise = null;
          return ixes;
        }
      } catch { continue; }
    }
    _ixFetchPromise = null;
    throw new Error('PeeringDB returned no IX data from any endpoint');
  })();
  _ixFetchPromise.catch(() => { _ixFetchPromise = null; });
  return _ixFetchPromise;
}

async function getFacList(): Promise<any[]> {
  if (_facCache && Date.now() - _facCache.ts < GLOBAL_CACHE_TTL) return _facCache.data;
  if (_facFetchPromise) return _facFetchPromise;
  _facFetchPromise = (async () => {
    // US facility list is ~1.3MB — needs generous timeout through proxy
    const urls = [
      `https://www.peeringdb.com/api/fac?country=US&depth=0`,
      `https://www.peeringdb.com/api/fac?depth=0`,
    ];
    for (const url of urls) {
      try {
        const res = await fetchWithTimeout(url, 45000);
        if (!res.ok) continue;
        const text = await res.text();
        if (text.startsWith('<') || !text.startsWith('{')) continue;
        const json = JSON.parse(text);
        const facs = (json.data || []).filter((f: any) => f.latitude && f.longitude).slice(0, MAX_CACHED_ENTRIES);
        if (facs.length > 0) {
          _facCache = { data: facs, ts: Date.now() };
          _facFetchPromise = null;
          return facs;
        }
      } catch { continue; }
    }
    _facFetchPromise = null;
    return [];
  })();
  _facFetchPromise.catch(() => { _facFetchPromise = null; });
  return _facFetchPromise;
}

export const peeringDbProvider: DataProvider<ConnectivityData> = {
  id: 'peeringdb-ix',
  name: 'PeeringDB IX Proximity',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  async fetch(
    lat,
    lng,
    _apiKey?: string,
    geo?: ProviderGeoContext,
  ): Promise<ProviderResult<ConnectivityData>> {
    throwIfAborted(geo?.signal);
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`peeringdb-ix` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. Distances are haversine'd from this point.
    // The list fetches are shared process-wide promises. Aborting their
    // transport for one report attempt would also fail unrelated concurrent
    // consumers, so race the attempt against the shared work. Cancellation
    // therefore stops this attempt immediately while an already-started shared
    // refresh is allowed to finish and populate the global cache.
    const facs = await raceWithAbort(getFacList(), geo?.signal);
    throwIfAborted(geo?.signal);

    let facilityCount50km = 0;
    let nearestFacDist = Infinity;
    let nearestFac: any = null;
    for (const fac of facs) {
      const d = haversineKm(lat, lng, fac.latitude, fac.longitude);
      if (d < 50) facilityCount50km++;
      if (d < nearestFacDist) {
        nearestFacDist = d;
        nearestFac = fac;
      }
    }

    // Prefer IX endpoints with coordinates; fall back to colo facilities.
    let nearestIxDist = Infinity;
    let nearestIx: any = null;
    let ixCount50km = 0;
    try {
      const ixes = await raceWithAbort(getIxList(), geo?.signal);
      throwIfAborted(geo?.signal);
      for (const ix of ixes) {
        const d = haversineKm(lat, lng, ix.latitude, ix.longitude);
        if (d < 50) ixCount50km++;
        if (d < nearestIxDist) {
          nearestIxDist = d;
          nearestIx = ix;
        }
      }
    } catch (error) {
      rethrowIfAborted(geo?.signal, error);
      /* IX list is supplementary — facility fallback below */
    }

    const usedIx = nearestIx != null && Number.isFinite(nearestIxDist);
    const nearestDist = usedIx ? nearestIxDist : nearestFacDist;
    const nearestName = usedIx ? nearestIx.name : (nearestFac?.name || 'Unknown');
    const nearestCity = (usedIx ? nearestIx.city : nearestFac?.city) || 'Unknown';

    if (!Number.isFinite(nearestDist)) {
      throw new Error('PeeringDB returned no facility or IX data');
    }

    const sourceKind = usedIx ? 'nearest IXP' : 'nearest colo facility';
    return {
      data: {
        nearestIxName: nearestName,
        nearestIxCity: nearestCity,
        nearestIxDistKm: parseFloat(nearestDist.toFixed(1)),
        ixCount50km,
        facilityCount50km,
        // CONNECTIVITY-ADVISORY — label the fallback IN THE PAYLOAD, not just
        // the source string: a colo facility substituted for the IX must never
        // be SCORED as an IX distance. Before this field the substitution was
        // machine-invisible — `sourceKind` said "nearest colo facility" in
        // prose while the data read like an IX. Display surfaces may still
        // show the facility; the scorer excludes it.
        nearestIxKind: (usedIx ? 'ixp' : 'colo-fallback') as 'ixp' | 'colo-fallback',
      },
      sources: [{
        field: 'connectivity',
        source: `PeeringDB — ${sourceKind}: ${nearestName} (${nearestCity}), ${nearestDist.toFixed(1)}km. ${ixCount50km} IXPs, ${facilityCount50km} colo facilities within 50km.`,
        url: 'https://www.peeringdb.com/',
        retrievedAt: new Date().toISOString(),
      }],
    };
  },
};
